import { Icon, Input, InputGroup, InputLeftElement, type InputGroupProps } from '@chakra-ui/react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';

type Props = InputGroupProps & {
  placeholder?: string;
  delay?: number;
  urlSync?: boolean;
  urlKey?: string;
  onCommit?: (value: string) => void;
};

const SearchInputWithUrlSync = forwardRef<HTMLInputElement, Props>(
  ({ placeholder = 'Search', delay = 600, urlKey = 'search', onCommit, ...styleProps }, ref) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [value, setValue] = useState(searchParams.get(urlKey) ?? '');
    const hasMountedRef = useRef(false);

    useEffect(() => {
      if (!hasMountedRef.current) {
        const initialValue = searchParams.get(urlKey) ?? '';
        if (initialValue.length > 0) {
          onCommit?.(initialValue);
        }
        hasMountedRef.current = true;
      }
    }, [searchParams, urlKey, onCommit]);

    const handleInputChange = useDebouncedCallback((nextValue: string) => {
      onCommit?.(nextValue);

      setSearchParams(
        (prevParams) => {
          const nextParams = new URLSearchParams(prevParams);

          if (nextValue.length > 0) {
            nextParams.set(urlKey, nextValue);
          } else {
            nextParams.delete(urlKey);
          }

          const page = searchParams.get('page') ?? '';
          if (page.length > 0 && page !== '1') {
            nextParams.set('page', '1');
          }

          return nextParams;
        },
        { replace: true },
      );
    }, delay);

    return (
      <SearchInputBase
        ref={ref}
        placeholder={placeholder}
        value={value}
        onValueChange={(nextValue) => {
          setValue(nextValue);
          handleInputChange(nextValue);
        }}
        {...styleProps}
      />
    );
  },
);

SearchInputWithUrlSync.displayName = 'SearchInputWithUrlSync';

const SearchInputLocal = forwardRef<HTMLInputElement, Props>(
  ({ placeholder = 'Search', delay = 600, onCommit, ...styleProps }, ref) => {
    const [value, setValue] = useState('');

    const handleInputChange = useDebouncedCallback((nextValue: string) => {
      onCommit?.(nextValue);
    }, delay);

    return (
      <SearchInputBase
        ref={ref}
        placeholder={placeholder}
        value={value}
        onValueChange={(nextValue) => {
          setValue(nextValue);
          handleInputChange(nextValue);
        }}
        {...styleProps}
      />
    );
  },
);

SearchInputLocal.displayName = 'SearchInputLocal';

const SearchInputBase = forwardRef<
  HTMLInputElement,
  InputGroupProps & { placeholder?: string; value: string; onValueChange: (value: string) => void }
>(({ placeholder, value, onValueChange, ...styleProps }, ref) => {
  return (
    <InputGroup size="sm" {...styleProps}>
      <InputLeftElement pointerEvents="none" h="full">
        <Icon as={SearchIcon} color="fg.muted" fontSize="sm" />
      </InputLeftElement>
      <Input
        type="search"
        size="sm"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        placeholder={placeholder}
        fontSize="sm"
        ref={ref}
        autoComplete="off"
      />
    </InputGroup>
  );
});

SearchInputBase.displayName = 'SearchInputBase';

export const SearchInput = forwardRef<HTMLInputElement, Props>(
  ({ urlSync = true, ...props }, ref) => {
    if (urlSync) {
      return <SearchInputWithUrlSync ref={ref} {...props} />;
    }
    return <SearchInputLocal ref={ref} {...props} />;
  },
);

SearchInput.displayName = 'SearchInput';
